<template>
  <div class="rounded-xl h-full bg-white cursor-pointer flex flex-col min-w-0">
    <div class="w-ful h-64 relative p-3">
      <span
        v-if="displayIsAvailable && displayIsNew"
        class="m-1 absolute bg-green-500 text-white text-sm font-semibold rounded-lg p-1"
        >{{ $t('page.new') }}</span
      >
      <span
        v-if="displayIsAvailable && displayInPromotion"
        class="m-1 absolute right-3 bg-red-500 text-white text-sm w-7 h-7 text-center font-semibold rounded-full p-1"
        >A</span
      >
      <span
        v-if="displayIsAvailable && hasDisplayDiscount"
        class="m-1 absolute bottom-0 left-3 font-semibold bg-red-500 text-white text-sm text-center rounded-lg p-1"
      >
        -{{ displayDiscountPercent }}%
      </span>
      <span
        v-if="detail?.has_video"
        class="m-1 absolute bg-white right-3 bottom-0 font-semibold text-white text-sm text-center rounded-full p-2"
        ><IconPlayRegular
      /></span>
      <img
        :src="detail?.thumbnail"
        class="object-fill w-full rounded"
        style="height: inherit"
        alt=""
      />
    </div>
    <div class="-mt-0.5 self-end mr-5 z-20 relative">
      <VueStarRating
        :rounded-corners="true"
        :star-size="15"
        active-color="#ffb300"
        inactive-color="#f2f2f2"
        :border-width="3"
        border-color="#ffb300"
        :show-rating="false"
        :read-only="true"
        :increment="0.5"
        :rating="detail?.rating"
      ></VueStarRating>
    </div>
    <div class="mx-3 space-y-1 min-w-0">
      <p class="text-base text-[#333] font-light truncate" :title="detail?.store?.name">
        {{ detail?.store?.name }}
      </p>

      <p class="text-xl text-main-color font-semibold truncate" :title="detail?.name">
        {{ detail?.name || '&emsp;' }}
      </p>

      <p
        v-if="!hasDisplayDiscount"
        class="text-[#409DBA] font-bold text-lg truncate"
        :title="`${formatNumber(displayPrice)} TMT`"
      >
        {{ formatNumber(displayPrice) }} TMT
      </p>

      <div class="min-w-0">
        <p
          class="text-[#409DBA] font-bold text-lg truncate"
          v-if="hasDisplayDiscount"
          :title="`${formatNumber(displayDiscountedPrice)} TMT ${formatNumber(displayPrice)} TMT`"
        >
          {{ formatNumber(displayDiscountedPrice) }} TMT
          <span class="line-through font-semibold text-lg text-[#CB3131]">
            {{ formatNumber(displayPrice) }} TMT
          </span>
        </p>
      </div>

      <div v-if="displayIsAvailable" class="flex font-semibold items-center gap-2">
        {{ $t('page.onCredit') }}
        <IconChecked v-if="displayForCredit" />
        <IconUnchecked v-else />
      </div>
    </div>
    <div class="mx-3 mt-auto py-3 flex justify-end items-center gap-5">
      <button
        v-if="!displayIsAvailable"
        class="bg-red-500 text-white w-full h-full rounded-lg font-medium text-lg"
      >
        {{ $t('page.outOfStock') }}
      </button>

      <template v-if="detail?.store?.can_online_order">
        <div v-if="displayIsAvailable && hasStocks" class="w-full">
          <SubmitBtn
            @onclick.stop.prevent="
              $router.push({ name: 'storeProductDetail', params: { id: detail?.id } })
            "
            :loading="loading"
            class="!w-full block text-center rounded-lg bg-main-color text-base py-4 text-white font-semibold !px-0 !py-3"
          >
            <p class="text-white text-base font-semibold">{{ $t('page.addToCart') }}</p>
          </SubmitBtn>
        </div>
        <div v-else-if="displayIsAvailable && !hasStocks" class="w-full">
          <SubmitBtn
            v-if="cartItem === null"
            @onclick.stop.prevent="addToCart"
            :loading="loading"
            class="!w-full block text-center rounded-lg bg-main-color text-base py-4 text-white font-semibold !px-0 !py-3"
          >
            <!-- <IconPlus></IconPlus> -->
            <p class="text-white text-base font-semibold">{{ $t('page.addToCart') }}</p>
          </SubmitBtn>
          <div v-else class="flex items-center gap-2 justify-between">
            <button
              @click.stop.prevent="updateCartItem(cartItem.quantity - 1)"
              class="rounded-lg inline-flex justify-center items-center py-4 px-4 bg-main-color ease-linear transition-all duration-150 hover:bg-accent-color"
            >
              <IconMinus svgClass="" pathClass="stroke-white" />
            </button>
            <input
              class="w-10 text-center text-xl rounded border max-md:w-14"
              readonly
              type="text"
              :value="cartItem.quantity"
            />
            <button
              @click.stop.prevent="updateCartItem(cartItem.quantity + 1)"
              class="rounded-lg inline-flex justify-center items-center py-4 px-4 bg-main-color ease-linear transition-all duration-150 hover:bg-accent-color"
            >
              <IconPlusRegular svgClass="" pathClass="stroke-white" />
            </button>
          </div>
        </div>
      </template>

      <button
        class="rounded-lg bg-[#409DBA] px-4 py-4 flex justify-center items-center cursor-pointer"
        @click.stop.prevent="likeAction(detail?.id)"
      >
        <IconHeart :class="{ 'fill-white': isLiked }"></IconHeart>
      </button>
    </div>
  </div>
</template>

<script>
import { i18n } from '@/i18n';
import api from '@/api';
import { mapState } from 'pinia';
import { useAppStore } from '@/store';
import IconPlus from '@icons/IconPlus.vue';
import IconPlusRegular from '@icons/IconPlusRegular.vue';
import IconHeart from '@icons/IconHeart.vue';
import IconChecked from '@icons/IconChecked.vue';
import IconPlayRegular from '@icons/IconPlayRegular.vue';
import IconMinus from '@icons/IconMinus.vue';
import IconUnchecked from '@icons/IconUnchecked.vue';
import VueStarRating from 'vue-star-rating';
import { formatNumber } from '@/helpers';
export default {
  name: 'StoreProductCard',
  props: {
    detail: {
      type: Object,
      default: () => ({}),
    },
  },
  components: {
    IconPlus,
    IconPlusRegular,
    IconHeart,
    IconChecked,
    IconUnchecked,
    VueStarRating,
    IconMinus,
    IconPlayRegular,
  },
  data() {
    return {
      loading: false,
      isLiked: false,
    };
  },
  methods: {
    formatNumber,
    likeAction(id) {
      this.isLiked = !this.isLiked;
      this.$emit('likeAction', id, this.isLiked);
    },
    async addToCart() {
      this.loading = true;
      try {
        let body = {
          product: this.detail.id,
          quantity: 1,
          session: this.getOrderSessionId,
        };
        const response = await api.post('mall/cart-items/', body);
        this.$toast.info(`${i18n.global.t('warning.CART_ADDED')}`);
        await this.$store.getSessionCart();
      } catch (e) {
        console.log(e);
      }
      this.loading = false;
    },
    deleteCartItem(qty) {
      this.$store.deleteCartItem(this.cartItem.id, qty).then(e => {
        this.loading = false;
      });
    },
    updateCartItem(qty) {
      this.loading = true;
      if (qty === 0) {
        this.deleteCartItem(this.cartItem.id, qty);
        return;
      }
      this.$store.updateCartItem(this.cartItem.id, qty).then(e => {
        this.loading = false;
      });
    },
  },
  computed: {
    ...mapState(useAppStore, ['getToken', 'getOrderSessionId', 'getCartItemById']),

    sortedStocks() {
      const stocks = Array.isArray(this.detail?.stocks) ? this.detail.stocks : [];

      return [...stocks].sort((a, b) => {
        return Number(a?.order ?? 0) - Number(b?.order ?? 0);
      });
    },

    firstStock() {
      return this.sortedStocks.length > 0 ? this.sortedStocks[0] : null;
    },

    hasStocks() {
      return this.firstStock !== null;
    },

    displayPrice() {
      return this.firstStock ? this.firstStock.price : this.detail?.price;
    },

    displayDiscountPercent() {
      return this.firstStock ? this.firstStock.discount_percent : this.detail?.discount_percent;
    },

    hasDisplayDiscount() {
      return Number(this.displayDiscountPercent || 0) > 0;
    },

    displayDiscountedPrice() {
      const price = Number(this.displayPrice || 0);
      const percent = Number(this.displayDiscountPercent || 0);

      return price - price * (percent / 100);
    },

    displayIsNew() {
      return this.firstStock ? this.firstStock.is_new : this.detail?.is_new;
    },

    displayIsAvailable() {
      return this.firstStock ? this.firstStock.is_available : this.detail?.is_available;
    },

    displayForCredit() {
      return this.firstStock ? this.firstStock.for_credit : this.detail?.for_credit;
    },

    displayInPromotion() {
      return this.firstStock ? this.firstStock.in_promotion : this.detail?.in_promotion;
    },

    cartItem() {
      return this.getCartItemById(this.detail?.id, null);
    },
  },
  created() {
    this.isLiked = this.detail?.is_liked;
  },
};
</script>
